<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import type School from '@/core/domain/school/core/School.ts'
import { useSchoolPeriodStore } from '@/presentation/stores/school/schoolPeriod.ts'
import { useAssignmentStore } from '@/presentation/stores/school/assignment.ts'
import { useToast } from 'primevue/usetoast'
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import PButton from 'primevue/button'
import PMessage from 'primevue/message'
import PProgressSpinner from 'primevue/progressspinner'
import PCheckbox from 'primevue/checkbox'
import PTag from 'primevue/tag'
import PDataTable from 'primevue/datatable'
import PColumn from 'primevue/column'
import { ClassroomDegree } from '@/core/domain/resources/enums/ClassroomDegree.ts'
import type {
  AssignedModuleEntity,
  AssignmentInputEntity
} from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import type { ValidModuleForAssignment } from '@/core/domain/school/assignment/ValidModuleForAssignment.ts'

// Props
const props = defineProps<{
  school: School
}>()

// Composables
const toast = useToast()
const schoolPeriodStore = useSchoolPeriodStore()
const assignmentStore = useAssignmentStore()

// Store refs
const { selectedSchoolPeriod } = storeToRefs(schoolPeriodStore)
const { assignments, validModules, assignmentsByDegree, validModulesByOrder, loading, error } =
  storeToRefs(assignmentStore)

// Local state
const localLoading = ref(false)
const localError = ref('')
const selectedModules = ref<Map<ClassroomDegree.Values, Set<number>>>(new Map())

// Initialize selected modules maps
const initializeSelectedModules = () => {
  for (const degree of Object.values(ClassroomDegree.Values)) {
    selectedModules.value.set(degree, new Set())
  }
}

// Computed
const isLoading = computed(
  () =>
    localLoading.value ||
    loading.value.fetchAssignments ||
    loading.value.fetchValidModules ||
    loading.value.createAssignment ||
    loading.value.updateAssignment
)

const errorMessage = computed(() => {
  if (localError.value && localError.value.trim() !== '') return localError.value
  if (error.value && error.value.message && error.value.message.trim() !== '')
    return error.value.message
  return null
})

const degrees = computed(() => [
  ClassroomDegree.Values.SECONDE,
  ClassroomDegree.Values.PREMIERE,
  ClassroomDegree.Values.TERMINALE
])

// Statistics per degree
const degreeStats = computed(() => {
  const stats = new Map()

  for (const degree of degrees.value) {
    const selectedCount = selectedModules.value.get(degree)?.size || 0
    const assignment = assignmentsByDegree.value.get(degree)
    const isLocked = assignment?.locked || false
    const minRequired = 3 // Minimum requis selon l'interface

    // Calculer la durée du diagnostic (fictive pour l'instant)
    const diagnosticDuration = selectedCount > 0 ? `${selectedCount * 15} minutes` : '0 minutes'

    stats.set(degree, {
      selectedCount,
      isLocked,
      minRequired,
      diagnosticDuration,
      canActivate: selectedCount >= minRequired,
      status: selectedCount >= minRequired ? 'valid' : 'invalid'
    })
  }

  return stats
})

// Methods
const loadData = async (forceRefresh = false) => {
  if (!selectedSchoolPeriod.value) return

  try {
    localLoading.value = true
    localError.value = ''

    // Utiliser la méthode optimisée du store
    await assignmentStore.loadInitialData(
      props.school.id,
      selectedSchoolPeriod.value.schoolPeriodId,
      forceRefresh
    )

    // Initialize selections based on existing assignments
    initializeSelectedModules()
    for (const assignment of assignments.value) {
      const moduleIds = assignment.assignedModules.map((m) => m.learningModuleId)
      selectedModules.value.set(assignment.degree, new Set(moduleIds))
    }
  } catch (err) {
    localError.value = 'Erreur lors du chargement des données'
    console.error('Erreur:', err)
  } finally {
    localLoading.value = false
  }
}

const toggleModule = (moduleId: number, degree: ClassroomDegree.Values) => {
  const stats = degreeStats.value.get(degree)
  if (stats?.isLocked) {
    toast.add({
      severity: 'warn',
      summary: 'Attention',
      detail: 'Les modules ont déjà été activés pour ce niveau.',
      life: 3000
    })
    return
  }

  const degreeModules = selectedModules.value.get(degree) || new Set()

  if (degreeModules.has(moduleId)) {
    degreeModules.delete(moduleId)
  } else {
    degreeModules.add(moduleId)
  }

  selectedModules.value.set(degree, new Set(degreeModules))
}

const saveSelection = async (degree: ClassroomDegree.Values) => {
  if (!selectedSchoolPeriod.value) return

  try {
    const degreeModules = selectedModules.value.get(degree) || new Set()
    const existingAssignment = assignmentsByDegree.value.get(degree)
    const idsByModule: Record<number, number> = {}
    if (existingAssignment?.assignedModules) {
      for (const module of existingAssignment.assignedModules) {
        idsByModule[module.learningModuleId] = module.id
      }
    }

    const assignedModules = Array.from(degreeModules)
      .map((moduleId) => ({ id: idsByModule[moduleId] ?? 0, learningModuleId: moduleId }))
      .filter(Boolean) as AssignedModuleEntity[]

    const assignmentData: AssignmentInputEntity = {
      id: existingAssignment?.id,
      schoolPeriodId: selectedSchoolPeriod.value.schoolPeriodId,
      degree,
      locked: false,
      assignedModules: assignedModules
    }

    if (existingAssignment) {
      await assignmentStore.updateAssignment(
        props.school.id,
        selectedSchoolPeriod.value.schoolPeriodId,
        existingAssignment.id,
        assignmentData
      )
    } else {
      await assignmentStore.createAssignment(
        props.school.id,
        selectedSchoolPeriod.value.schoolPeriodId,
        assignmentData
      )
    }
  } catch (err) {
    console.error('Erreur lors de la sauvegarde:', err)
  }
}

const activateSelection = async (degree: ClassroomDegree.Values) => {
  if (!selectedSchoolPeriod.value) return

  try {
    const degreeModules = selectedModules.value.get(degree) || new Set()
    const existingAssignment = assignmentsByDegree.value.get(degree)
    const idsByModule: Record<number, number> = {}
    if (existingAssignment?.assignedModules) {
      for (const module of existingAssignment.assignedModules) {
        idsByModule[module.learningModuleId] = module.id
      }
    }

    const assignedModules = Array.from(degreeModules)
      .map((moduleId) => ({ id: idsByModule[moduleId] ?? 0, learningModuleId: moduleId }))
      .filter(Boolean) as AssignedModuleEntity[]

    const assignmentData: AssignmentInputEntity = {
      id: existingAssignment?.id,
      schoolPeriodId: selectedSchoolPeriod.value.schoolPeriodId,
      degree,
      locked: true, // Verrouiller lors de l'activation
      assignedModules: assignedModules
    }

    if (existingAssignment) {
      await assignmentStore.updateAssignment(
        props.school.id,
        selectedSchoolPeriod.value.schoolPeriodId,
        existingAssignment.id,
        assignmentData
      )
    } else {
      await assignmentStore.createAssignment(
        props.school.id,
        selectedSchoolPeriod.value.schoolPeriodId,
        assignmentData
      )
    }
  } catch (err) {
    console.error("Erreur lors de l'activation:", err)
  }
}

const isModuleSelected = (moduleId: number, degree: ClassroomDegree.Values): boolean => {
  return selectedModules.value.get(degree)?.has(moduleId) || false
}

const getModuleCheckboxId = (moduleId: number, degree: ClassroomDegree.Values): string => {
  return `module-${moduleId}-${degree}`
}

// Watchers
watch(
  () => selectedSchoolPeriod.value,
  () => loadData(),
  { immediate: true }
)

// Lifecycle
onMounted(() => {
  // Les périodes scolaires sont déjà chargées par le router guard
  // Les données du tab sont pré-chargées par le composant parent (VSchoolsSchool)
  initializeSelectedModules()

  // Ne charger que si les données ne sont pas déjà présentes
  if (assignments.value.length === 0 && validModules.value.length === 0) {
    loadData()
  }
})
</script>

<template>
  <c-base-block :toggleable="false" title="Gestion des modules">
    <!-- Messages d'erreur -->
    <p-message v-if="errorMessage" severity="error" class="mb-4 w-full">
      {{ errorMessage }}
    </p-message>

    <!-- Loading spinner -->
    <div v-if="isLoading" class="flex justify-center p-6">
      <p-progress-spinner strokeWidth="4" />
    </div>

    <!-- Contenu principal -->
    <template v-else>
      <!-- Instructions -->
      <div class="mb-6 flex flex-col gap-1 rounded-lg p-4">
        <p class="text-sm">
          Dans ce tableau,
          <strong>sélectionnez les modules que vous souhaitez faire réaliser</strong> à vos élèves.
        </p>
        <p class="text-sm">
          Chaque niveau doit avoir au moins <strong>3 modules sélectionnés</strong> : si vous n'en
          sélectionnez aucun, le niveau concerné ne sera pas actif.
        </p>
        <p class="text-sm">
          Lorsque vous activez la sélection, elle est
          <strong>définitive pour tout le niveau</strong>, pendant
          <strong>toute la période scolaire</strong>.
        </p>
      </div>

      <!-- Tableau des modules avec DataTable -->
      <p-data-table
        :value="validModulesByOrder"
        :loading="isLoading"
        stripedRows
        showGridlines
        tableStyle="min-width: 50rem"
        emptyMessage="Aucun module disponible"
        class="w-full cursor-default"
        group-rows-by="learningCourseId"
        row-group-mode="subheader"
        :pt="{
          rowgroupheadercell: {
            colspan: Object.keys(ClassroomDegree.Values).length + 1,
          },
          rowgroupfootercell: {
            colspan: Object.keys(ClassroomDegree.Values).length + 1
          }
        }"
      >
        <template #empty>
          <div class="p-6 text-center">Aucun module disponible</div>
        </template>

        <template #loading>
          <div class="p-6 text-center">
            <p-progress-spinner />
            <div>Chargement des modules...</div>
          </div>
        </template>

        <!-- Colonne Module -->
        <p-column field="title" header="Module" style="width: 30%">
          <template #body="{ data }: { data: ValidModuleForAssignment }">
            <div class="flex flex-col gap-1">
              <h4 class="font-medium" :data-prefix-order="`${data.learningCourseId}.${data.learningCourseOrder}.${data.order}`">{{ data.title }}</h4>
              <router-link
                :to="{
                  name: 'naboo-content-module',
                  params: { courseId: data.learningCourseId, moduleId: data.id }
                }"
                class="text-primary-600 hover:text-primary-800 text-xs"
              >
                Voir les contenus →
              </router-link>
            </div>
          </template>
        </p-column>

        <!-- Colonnes pour chaque degré -->
        <p-column v-for="degree in degrees" :key="`col_${degree}`" style="width: 23%; text-align: center">
          <template #header>
            <div class="flex flex-col gap-2">
              <h3 class="mb-2 text-lg font-semibold">
                Classes de {{ ClassroomDegree.label(degree) }}
              </h3>
              <div class="flex flex-col gap-2">
                <p-tag
                  :value="`${degreeStats.get(degree)?.selectedCount || 0} modules sélectionnés`"
                  :severity="degreeStats.get(degree)?.status === 'valid' ? 'success' : 'warning'"
                  class="mx-auto w-fit"
                />
                <p class="text-surface-600 dark:text-surface-400 text-xs">
                  Durée du diagnostic : {{ degreeStats.get(degree)?.diagnosticDuration }}
                </p>
              </div>
            </div>
          </template>
          <template #body="{ data }: { data: ValidModuleForAssignment }">
            <div class="flex justify-center">
              <p-checkbox
                :inputId="getModuleCheckboxId(data.id, degree)"
                :modelValue="isModuleSelected(data.id, degree)"
                :disabled="degreeStats.get(degree)?.isLocked"
                @update:modelValue="toggleModule(data.id, degree)"
                binary
              />
            </div>
          </template>
        </p-column>

        <template #groupheader="{ data }: { data: ValidModuleForAssignment }">
          <div class="flex items-center gap-2">
            <h3 class="w-fit font-bold">{{data.learningCourseTitle}}</h3>
          </div>
        </template>

        <template #groupfooter>
          <div class="flex justify-end font-bold w-full" :colspan="4"></div>
        </template>
      </p-data-table>

      <!-- Footer avec boutons alignés sur les colonnes -->
      <div class="mt-4 grid grid-cols-4 gap-0" style="grid-template-columns: 30% 23% 23% 23%">
        <!-- Colonne vide pour aligner avec la colonne Module -->
        <div></div>

        <!-- Boutons pour chaque degré -->
        <div v-for="degree in degrees" :key="degree" class="flex flex-col items-center gap-2 px-2">
          <!-- Messages d'état -->
          <div
            v-if="degreeStats.get(degree)?.isLocked"
            class="mb-2 text-center text-sm text-green-600 dark:text-green-400"
          >
            Les modules ont été activés
          </div>
          <div
            v-else-if="!degreeStats.get(degree)?.canActivate"
            class="mb-2 text-center text-sm text-orange-600 dark:text-orange-400"
          >
            Il faut au moins {{ degreeStats.get(degree)?.minRequired }} modules pour sauvegarder ou
            activer la sélection.
          </div>

          <!-- Boutons d'action -->
          <div class="flex flex-col items-center gap-2">
            <p-button
              label="Enregistrer la sélection"
              icon="pi pi-save"
              outlined
              size="small"
              :disabled="degreeStats.get(degree)?.isLocked || isLoading"
              @click="saveSelection(degree)"
            />
            <p-button
              label="Activer la sélection"
              icon="pi pi-check"
              size="small"
              :disabled="
                !degreeStats.get(degree)?.canActivate ||
                degreeStats.get(degree)?.isLocked ||
                isLoading
              "
              @click="activateSelection(degree)"
            />
          </div>
        </div>
      </div>
    </template>
  </c-base-block>
</template>
